/**
 * 매물탐색 API 응답 어댑터
 *
 * 백엔드 응답(snake_case)을 프론트엔드(camelCase)로 변환한다.
 * 기존 API와 V2 API 모두 동일한 형태로 정규화한다.
 */

// snake_case → camelCase 키 매핑 (매물 아이템 공통)
const ITEM_KEY_MAP = {
  address_open: "addressOpen",
  building_name: "buildingName",
  deal_type: "dealType",
  e_rand_type: "randType",
  rand_type: "randType",
  estate_type: "estateType",
  estate_type_tag: "estateTypeTag",
  group_key: "groupKey",
  marker_key: "markerKey",
  group_count: "groupCount",
  supply_area: "supplyArea",
  land_area: "landArea",
  floor_area: "floorArea",
  floor_cnt: "floorCnt",
  floor_total: "floorTotal",
  under_floor: "underFloor",
  floor_type: "floorType",
  land_purpose: "landPurpose",
  creation_date: "creationDate",
  agent_name: "agentName",
  agent_photo: "agentPhoto",
  agent_office_name: "agentOfficeName",
  agent_position: "agentPosition",
  agent_tier: "agentTier",
  agent_user: "agentUser",
  owner_verified: "ownerVerified",
  agent_group_count: "agentGroupCount",
  agent_group_items: "agentGroupItems",
  select_type: "selectType",
};

// 최상위 응답 키 매핑 (snake_case → camelCase, 이미 camelCase인 것은 통과)
const TOP_KEY_MAP = {
  total_count: "totalCount",
  has_next: "hasNext",
  zoom_required: "zoomRequired",
  real_count: "realCount",
  group_key: "groupKey",
};

/**
 * 객체의 키를 매핑 테이블 기준으로 변환한다.
 * 매핑에 없는 키는 그대로 유지한다.
 */
function mapKeys(obj, keyMap) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return obj;
  const result = {};
  for (const [k, v] of Object.entries(obj)) {
    const newKey = keyMap[k] || k;
    result[newKey] = v;
  }
  return result;
}

/**
 * 매물 아이템 하나를 camelCase로 변환
 */
function adaptItem(item) {
  if (!item) return item;
  const mapped = mapKeys(item, ITEM_KEY_MAP);

  // 중첩 배열 변환 (agentGroupItems, list 등)
  if (Array.isArray(mapped.agentGroupItems)) {
    mapped.agentGroupItems = mapped.agentGroupItems.map(adaptItem);
  }
  if (Array.isArray(mapped.list)) {
    mapped.list = mapped.list.map((sub) => mapKeys(sub, ITEM_KEY_MAP));
  }

  return mapped;
}

/**
 * list API 응답 정규화
 * 기존 API / V2 API 모두 동일한 형태로 반환:
 * { data: [...], count, totalCount, offset, hasNext }
 */
export function adaptListResponse(res) {
  if (!res) return res;
  const top = mapKeys(res, TOP_KEY_MAP);

  // 기존 API는 data 안에 이미 camelCase가 섞여있을 수 있음
  const items = Array.isArray(top.data) ? top.data : [];
  top.data = items.map(adaptItem);

  return top;
}

/**
 * detail API 응답 정규화
 * 기존 API: { list: [...] } → { data: [...] }
 * V2 API: { data: [...] }
 */
export function adaptDetailResponse(res) {
  if (!res) return res;
  const top = mapKeys(res, TOP_KEY_MAP);

  // 기존 API는 "list" 키를 사용
  const items = Array.isArray(top.data)
    ? top.data
    : Array.isArray(top.list)
      ? top.list
      : [];

  top.data = items.map(adaptItem);
  if (top.list && top.data !== top.list) delete top.list;

  return top;
}

/**
 * map API 응답 정규화
 * 마커 아이템 내부의 list(개별 매물)도 변환
 */
export function adaptMapResponse(res) {
  if (!res) return res;
  const top = mapKeys(res, TOP_KEY_MAP);

  const items = Array.isArray(top.data) ? top.data : [];
  top.data = items.map((marker) => {
    const mapped = mapKeys(marker, ITEM_KEY_MAP);
    if (Array.isArray(mapped.list)) {
      mapped.list = mapped.list.map((sub) => mapKeys(sub, ITEM_KEY_MAP));
    }
    return mapped;
  });

  return top;
}
